<template>
    <div v-if="model">
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-input
                        :disabled="additionals.geraCodigoAutomatico"
                        :validations="validations.codigoMaterial"
                        label="Código"
                        v-model="model.codigoMaterial"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.descricao"
                        label="Descrição"
                        uppercase
                        v-model="model.descricao"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-select
                        :options="form.grupos"
                        :validations="validations.grupoId"
                        @change="handleGrupoChange()"
                        label="Grupo"
                        show-default-option
                        text-field="nome"
                        v-model="model.grupoId"
                        value-field="id"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        :disabled="!model.grupoId"
                        :options="subgrupos"
                        label="Subgrupo"
                        show-default-option
                        text-field="nome"
                        v-model="model.subgrupoId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-select
                        :options="form.apresentacoes"
                        :validations="validations.apresentacaoId"
                        label="Apresentação da Embalagem e Unidade de Fornecimento"
                        show-default-option
                        text-field="nome"
                        v-model="model.apresentacaoId"
                        value-field="id"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-form-input
                                :validations="validations.quantidade"
                                class="quantidade"
                                name="Quantidade"
                                number
                                placeholder="Quantidade"
                                v-model="model.quantidade"
                            />

                            <rkm-form-select
                                :options="form.apresentacaoUnidadesMedida"
                                :validations="validations.apresentacaoUnidadeMedidaId"
                                name="Unidade de medida"
                                show-default-option
                                text-field="nome"
                                v-model="model.apresentacaoUnidadeMedidaId"
                                value-field="id"
                            />
                        </rkm-input-group-append>
                    </rkm-form-select>
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-form-checkbox
                        :label="false"
                        inline
                        v-model="model.possuiLote"
                    >
                        Possui lote
                    </rkm-form-checkbox>

                    <rkm-form-checkbox
                        :label="false"
                        inline
                        v-model="model.isAtivo"
                    >
                        Ativo
                    </rkm-form-checkbox>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="2">
                    <rkm-form-input
                        :validations="validations.quantidadeGotasMl"
                        label="Conversão ml para gotas"
                        ref="quantidadeGotasMl"
                        v-model="model.quantidadeGotasMl"
                        v-money="money"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :validations="validations.periodoDispensacaoAdicionalDias"
                        append="dias"
                        label="Configurar período de dispensação adicional"
                        number
                        v-model="model.periodoDispensacaoAdicionalDias"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="isMaterial">
                <rkm-col cols="12">
                    <rkm-form-input-autocomplete
                        :repository="repositories.materialBnafar"
                        :search-fields="fields.materialBnafar"
                        :text-field="(p) => `${p.codigo ? `${p.codigo} - ` : ''}${p.descricao} - ${tipoDescricao(p.tipo)}`"
                        :validations="validations.materialBnafar"
                        resultsLimit="50"
                        label="Cadastro de materiais (CATMAT)"
                        v-model="model.materialBnafar"
                    />
                </rkm-col>
            </rkm-row>

            <template v-if="model.isMedicamento">
                <rkm-area type="fieldset" title='Medicamento'>
                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-input-autocomplete
                                :repository="repositories.material.anvisaSubstanciaControleEspecial"
                                :search-fields="fields.anvisaSubstanciaControleEspecial"
                                @select="handleAnvisaSubstanciaControleEspecialSelect"
                                text-field="descricao"
                                resultsLimit="50"
                                label="Portaria 344"
                                v-model="model.anvisaSubstanciaControleEspecial"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :options="form.receitaTipos"
                                :validations="validations.receitaTipoId"
                                label="Tipo de receita"
                                show-default-option
                                text-field="nome"
                                v-model="model.receitaTipoId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :disabled="naoPodeAlterarLivroRegistro"
                                :options="form.livrosRegistro"
                                :validations="validations.livroRegistroId"
                                label="Livro"
                                show-default-option
                                text-field="nome"
                                v-model="model.livroRegistroId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :disabled="naoPodeAlterarLivroRegistroTipo"
                                :options="livroRegistroTipos"
                                label="Tipo de livro"
                                show-default-option
                                text-field="nome"
                                v-model="model.livroRegistroTipoId"
                                value-field="id"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="3">
                            <rkm-form-select
                                :options="form.formasFarmaceutica"
                                :validations="validations.formaFarmaceuticaId"
                                label="Forma farmacêutica"
                                show-default-option
                                text-field="nome"
                                v-model="model.formaFarmaceuticaId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :options="form.viasAdministracao"
                                :validations="validations.viaAdministracaoId"
                                label="Via administração"
                                show-default-option
                                text-field="nome"
                                v-model="model.viaAdministracaoId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :options="form.apresentacaoUnidadesMedida"
                                label="Unidade de medida"
                                show-default-option
                                text-field="nome"
                                v-model="model.prescricaoUnidadeMedidaId"
                                value-field="id"
                            />
                        </rkm-col>

                        <rkm-col cols="3">
                            <rkm-form-select
                                :options="form.obmRenames"
                                label="RENAME"
                                show-default-option
                                text-field="descricao"
                                v-model="model.obmRenameId"
                                value-field="id"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col cols="12">
                            <rkm-form-input-autocomplete
                                :repository="repositories.materialBnafar"
                                :search-fields="fields.materialBnafar"
                                :text-field="(p) => `${p.codigo ? `${p.codigo} - ` : ''}${p.descricao} - ${tipoDescricao(p.tipo)}`"
                                :validations="validations.materialBnafar"
                                resultsLimit="50"
                                label="Cadastro de materiais (CATMAT)"
                                v-model="model.materialBnafar"
                            />
                        </rkm-col>
                    </rkm-row>

                    <rkm-row>
                        <rkm-col>
                            <rkm-form-checkbox
                                inline
                                v-model="model.isExibirReceita"
                            >
                                Exibir na receita médica
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isExibirPrescricaoInterna"
                            >
                                Exibir na prescrição interna
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isProgramaRemedioEmCasa"
                            >
                                Programa remédio em casa
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isAltoCusto"
                            >
                                Alto custo
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isAcaoJudicial"
                            >
                                Ação judicial
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isRemume"
                            >
                                REMUME
                            </rkm-form-checkbox>

                            <rkm-form-checkbox
                                inline
                                v-model="model.isLaudoLme"
                            >
                                Laudo LME
                            </rkm-form-checkbox>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </template>
        </rkm-form>

        <BoxIntegracaoBnafarMedicamento :data="{model}" />
    </div>
</template>

<script>
// Components.
import BoxIntegracaoBnafarMedicamento from './components/integracao-bnafar-medicamento/Index'

// Vuex.
import { mapActions } from 'vuex'

// Mixins.
import { repository, stringCase, object, error, formats } from '@/mixins'

// Directives.
import { VMoney } from 'v-money'

// Enums
import tipoGrupo from '@/enums/cadastro/grupo/tipoGrupo'
import unidadeMedida from '@/enums/cadastro/unidadeMedida'
import livroRegistro from '@/enums/cadastro/material/livroRegistro'
import livroRegistroTipo from '@/enums/cadastro/material/livroRegistroTipo'

export default {
    components: {BoxIntegracaoBnafarMedicamento},
    computed: {
        fields() {

            return {
                materialBnafar: [
                    { key: 'codigo', searchable: true },
                    { key: 'descricao', searchable: true }
                ],
                anvisaSubstanciaControleEspecial: [
                    { key: 'descricao', searchable: true }
                ],
            }
        },
        validations() {

            return {
                codigoMaterial: {
                    max: 10,
                    numeric: true,
                },
                descricao: {
                    required: true,
                    min: 3,
                    max: 100,
                },
                grupoId: {
                    required: true,
                },
                apresentacaoId: {
                    required: true,
                },
                quantidade: {
                    required: true,
                    max: 6,
                    min_value: 1,
                },
                apresentacaoUnidadeMedidaId: {
                    required: true,
                },
                quantidadeGotasMl: {
                    max: 7,
                },
                periodoDispensacaoAdicionalDias: {
                    max: 4,
                },
                receitaTipoId: {
                    required: this.model.isMedicamento
                },
                formaFarmaceuticaId: {
                    required: this.model.isMedicamento
                },
                viaAdministracaoId: {
                    required: this.model.isMedicamento
                },
                materialBnafar: {
                    required: this.model.integracaoBnafarMedicamentos.length > 0
                },
            }
        },
        subgrupos() {

            if (this.model.grupoId == null) return []

            return this.form.subgrupos.filter(s => s.grupoId === this.model.grupoId)
        },
        isMaterial() {
            const grupo = this.getGrupo(this.model.grupoId)

            return grupo && grupo.grupoTipoId === this.enums.tipoGrupo.MATERIAL
        },
        livroRegistroTipos() {

            const ids = this.getLivroRegistroTipoIds(this.model.livroRegistroId)

            return this.form.livroRegistroTipos.filter(t => ids.includes(t.id) || t.id === this.model.livroRegistroTipoId)
        },
        naoPodeAlterarLivroRegistro() {
            return this.form.hasVinculoLivro
        },
        naoPodeAlterarLivroRegistroTipo() {
            return this.form.hasVinculoLivro || this.getLivroRegistroTipoIds(this.model.livroRegistroId).length === 0
        }
    },
    created() {

        this.repositories = {
            material: this.getConectaSusRepository('cadastro.material'),
            materialBnafar: this.getConectaSusRepository('cadastro.materialBnafar'),
        }

        this.load()
    },
    data: () => ({
        additionals: null,
        model: null,
        enums: { tipoGrupo, unidadeMedida, livroRegistro, livroRegistroTipo },
        labelVolumeUnidadeMedida: null,
        money: {
            decimal: ',',
            thousands: '.',
            suffix: '',
            precision: 3,
            masked: false
        },
    }),
    directives: { money: VMoney },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            this.setLoading()

            if (this.id) {

                this.repositories.material.edit(this.id)
                    .then(({ data }) => {

                        if (data.model) {
                            this.additionals = this.clone(data.additionals)
                            this.form = this.clone(data.form)
                            this.model = this.clone(data.model)
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())

            } else {

                this.repositories.material.create()
                    .then(({ data }) => {

                        if (data.model) {
                            this.additionals = this.clone(data.additionals)
                            this.form = this.clone(data.form)
                            this.model = this.clone(data.model)
                        }
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }


        },
        save() {

            this.$refs.Form.validate()
                .then(valid => {

                    if (!valid) return

                    this.setLoading()

                    const request = this.id
                        ? this.repositories.material.update(this.id, this.prepareData())
                        : this.repositories.material.store(this.prepareData())

                    request
                        .then(({ data }) => {

                            this.showSaveNotification()
                            this.$emit('save', data)
                        })
                        .catch(this.handleError)
                        .finally(() => this.unsetLoading())
                })
        },
        prepareData() {

            const data = this.clone(this.model)

            data.materialBnafarId = data.materialBnafar && data.materialBnafar.id
            data.anvisaSubstanciaControleEspecialId = data.anvisaSubstanciaControleEspecial && data.anvisaSubstanciaControleEspecial.id

            data.quantidadeGotasMl = this.formatSumMoney(data.quantidadeGotasMl)

            return this.camelObjectKeysToSnakeRecursive(data)
        },
        handleGrupoChange() {

            this.model.subgrupoId = null
            this.model.materialBnafar = null
        },
        getGrupo(id) {

            if (id == null) return null

            return this.form.grupos.find(g => g.id === id)
        },
        getLivroRegistroTipoIds(livroRegistroId) {

            const { livroRegistro, livroRegistroTipo } = this.enums

            const tiposPorLivro = {
                [livroRegistro.ENTORPECENTES]: [livroRegistroTipo.A1, livroRegistroTipo.A2],
                [livroRegistro.PSICOTROPICOS]: [livroRegistroTipo.A3, livroRegistroTipo.B1, livroRegistroTipo.B2],
                [livroRegistro.CONTROLE_ESPECIAL]: [livroRegistroTipo.C1, livroRegistroTipo.C2, livroRegistroTipo.D1],
                [livroRegistro.IMUNOSSUPRESSORES]: [livroRegistroTipo.C3],
                [livroRegistro.ANTI_RETROVIRAIS]: [livroRegistroTipo.C4],
                [livroRegistro.ANABOLIZANTE]: [livroRegistroTipo.C5],
            }

            return tiposPorLivro[livroRegistroId] || []
        },
        resetModelMedicamento() {

            this.model.receitaTipoId = null
            this.model.livroRegistroId = null
            this.model.livroRegistroTipoId = null
            this.model.formaFarmaceuticaId = null
            this.model.viaAdministracaoId = null
            this.model.materialBnafarId = null
            this.model.obmRenameId = null
            this.model.materialBnafar = null
            this.model.isExibirReceita = false
            this.model.isExibirPrescricaoInterna = false
            this.model.isProgramaRemedioEmCasa = false
            this.model.isAltoCusto = false
            this.model.isAcaoJudicial = false
            this.model.isRemume = false
            this.model.isLaudoLme = false
        },
        tipoDescricao(tipo) {
            switch (tipo) {
                case 'B':
                    return 'Básico';
                case 'E':
                    return 'Especializado';
                case 'S':
                    return 'Estratégico';
                case 'O':
                    return 'Outros';
                default:
                    return 'Desconhecido';
            }
        },
        handleAnvisaSubstanciaControleEspecialSelect() {
            const tipoReceita = this.form.receitaTipos.find(t => t.id === this.model.anvisaSubstanciaControleEspecial.receitaTipoId)
            const livroRegistro = this.form.livrosRegistro.find(l => l.id === this.model.anvisaSubstanciaControleEspecial.livroRegistroId)
            const livroRegistroTipo = this.form.livroRegistroTipos.find(t => t.id === this.model.anvisaSubstanciaControleEspecial.livroRegistroTipoId)

            let listItems = `<li><strong>Tipo de receita:</strong> ${tipoReceita.nome}</li>`

            if (!this.naoPodeAlterarLivroRegistro) {
                listItems = listItems.concat(`<li><strong>Livro:</strong> ${livroRegistro.nome}</li>`)
            }

            if (!this.naoPodeAlterarLivroRegistro) {
                listItems = listItems.concat(`<li><strong>Tipo de livro:</strong> ${livroRegistroTipo.nome}</li>`)
            }

            this.showConfirmation(
                `Este medicamento possui vínculo com o(s) seguinte(s) dados:<br><br>
                <ul>${listItems}</ul>
                Deseja selecionar estes dados?`,
                async () => {
                    this.model.receitaTipoId = this.model.anvisaSubstanciaControleEspecial.receitaTipoId

                    if (!this.naoPodeAlterarLivroRegistro) {
                        this.model.livroRegistroId = this.model.anvisaSubstanciaControleEspecial.livroRegistroId
                    }

                    if (!this.naoPodeAlterarLivroRegistroTipo) {
                        this.model.livroRegistroTipoId = this.model.anvisaSubstanciaControleEspecial.livroRegistroTipoId
                    }
                }
            )
        }
    },
    mixins: [ repository, stringCase, object, error, formats ],
    props: {
        id: { type: Number },
    },
    watch: {
        'model.grupoId'(newValue, oldValue) {

            const previousGrupo = this.getGrupo(oldValue)
            const currentGrupo = this.getGrupo(newValue)

            const isPreviousGrupoMedicamento = previousGrupo && previousGrupo.grupoTipoId === this.enums.tipoGrupo.MEDICAMENTO
            const isCurrentGrupoMedicamento = currentGrupo && currentGrupo.grupoTipoId === this.enums.tipoGrupo.MEDICAMENTO

            if (isPreviousGrupoMedicamento && !isCurrentGrupoMedicamento) {

                const fnSim = async () => {
                    this.model.isMedicamento = false
                    this.resetModelMedicamento()
                }

                this.showConfirmation(
                    "Ao selecionar outro grupo, as informações da box medicamento serão perdidas. Deseja continuar?",
                    fnSim,
                    async () =>  this.model.grupoId = oldValue,
                )
            }

            if (isCurrentGrupoMedicamento) {

                this.model.isMedicamento = true
            }
        },
        'model.livroRegistroId'(id, oldId) {

            if (oldId === undefined) {

                const isTalidomida = id === this.enums.livroRegistro.TALIDOMIDA
                const isSemLivro = id === null && !this.form.hasVinculoLivro

                if (isTalidomida || isSemLivro) {

                    this.model.livroRegistroTipoId = null
                }

                return
            }

            if (this.form.hasVinculoLivro) return

            if (!this.getLivroRegistroTipoIds(id).includes(this.model.livroRegistroTipoId)) {

                this.model.livroRegistroTipoId = null
            }
        },
    }
}
</script>

<style scoped>
.quantidade {
    max-width: 160px;
}
</style>